import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { adminApi } from '../../../utils/api';
import { Spinner, SearchBar, Pagination } from '../shared';

const fmtCost = (n) => `$${(n || 0).toFixed(4)}`;
const fmtDate = (d) => {
    if (!d) return '-';
    return new Date(d).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
    });
};

export default function UserSessionsTab({ userId }) {
    const [sessions, setSessions] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [offset, setOffset] = useState(0);
    const limit = 50;

    useEffect(() => {
        loadSessions();
    }, [userId, offset, search]);

    const loadSessions = async () => {
        setLoading(true);
        try {
            const result = await adminApi.getUserSessions(userId, { limit, offset, search });
            setSessions(result.sessions || []);
            setTotal(result.total || 0);
        } catch (err) {
            console.error('Failed to load sessions:', err);
        } finally {
            setLoading(false);
        }
    };

    if (loading && sessions.length === 0) {
        return <Spinner text="Loading sessions..." center />;
    }

    return (
        <div className="h-full flex flex-col">
            <div className="shrink-0 mb-4">
                <SearchBar
                    value={search}
                    onChange={setSearch}
                    onSearch={() => setOffset(0)}
                    placeholder="Search sessions..."
                />
            </div>

            <div className="flex-1 overflow-y-auto">
                {sessions.length > 0 ? (
                    <div className="border border-gray-200 rounded-lg overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="bg-gray-50 border-b border-gray-200">
                                    <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500">Session Name</th>
                                    <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500">Created</th>
                                    <th className="text-right px-4 py-2.5 text-xs font-medium text-gray-500">Messages</th>
                                    <th className="text-right px-4 py-2.5 text-xs font-medium text-gray-500">Cost</th>
                                    <th className="text-right px-4 py-2.5 text-xs font-medium text-gray-500">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {sessions.map((s) => (
                                    <tr key={s.session_id} className="hover:bg-gray-50">
                                        <td className="px-4 py-2.5">
                                            <Link
                                                to={`/admin/sessions?session_id=${s.session_id}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-blue-600 hover:text-blue-800 hover:underline truncate max-w-md block"
                                            >
                                                {s.session_name}
                                            </Link>
                                        </td>
                                        <td className="px-4 py-2.5 text-xs text-gray-500">{fmtDate(s.created_at)}</td>
                                        <td className="px-4 py-2.5 text-right text-gray-700">{s.message_count}</td>
                                        <td className="px-4 py-2.5 text-right font-mono text-gray-700">{fmtCost(s.cost)}</td>
                                        <td className="px-4 py-2.5 text-right">
                                            <Link
                                                to={`/admin/sessions?session_id=${s.session_id}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-xs text-blue-600 hover:text-blue-700"
                                            >
                                                View
                                            </Link>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <p className="text-sm text-gray-400 text-center py-12">No sessions found</p>
                )}
            </div>

            {total > limit && (
                <div className="shrink-0 mt-4">
                    <Pagination total={total} limit={limit} offset={offset} onChange={setOffset} />
                </div>
            )}
        </div>
    );
}
